import { useState, useEffect } from "react";

interface HeroSectionProps {
  lang?: "en" | "it";
}

// Removed the grid images and simplified the slides array
const slides = [
  {
    bg: "https://i.postimg.cc/wxczRpDs/firstimage.png",
  },
  {
    bg: "https://i.postimg.cc/8ctPRQJ0/Chat-GPT-Image-May-21-2026-02-38-26-AM.png",
  },
];

const copy = {
  en: {
    badge: "Official Chamber of Commerce",
    heading: (
      <>
        Strengthening <br className="hidden sm:block" /> Italy –
        Bangladesh
        <br />
        <span className="italic font-medium">Trade &amp; Investment</span>
      </>
    ),
    subheading:
      "A premier bilateral platform connecting entrepreneurs, investors, and institutions across two of the world's most dynamic economies.",
    ctaPrimary: "Become a Member",
    ctaSecondary: "Explore Opportunities",
  },
  it: {
    badge: "Camera di Commercio Ufficiale",
    heading: (
      <>
        Rafforzare il <br className="hidden sm:block" /> Commercio Italia –
        Bangladesh
        <br />
        <span className="italic font-medium">e gli Investimenti</span>
      </>
    ),
    subheading:
      "La Camera di Commercio Italia-Bangladesh (BICCI) è la piattaforma bilaterale di riferimento che collega imprenditori, investitori e istituzioni tra due delle economie più dinamiche al mondo.",
    ctaPrimary: "Diventa Membro",
    ctaSecondary: "Scopri le Opportunità",
  },
};

export default function HeroSection({ lang = "en" }: HeroSectionProps) {
  const t = copy[lang];
  const [activeIdx, setActiveIdx] = useState(0); // Controls background
  const [contentIdx, setContentIdx] = useState(0); // Controls text
  const [isFading, setIsFading] = useState(false); // Controls the hide/show CSS animation
  const [isAnimating, setIsAnimating] = useState(false); // Prevents spam clicking

  const goTo = (next: number) => {
    if (next === activeIdx || isAnimating) return;

    setIsAnimating(true);
    setIsFading(true); // 1. Start fading out content
    setActiveIdx(next); // 2. Start crossfading background immediately

    // 3. Swap text while invisible, then fade back in
    setTimeout(() => {
      setContentIdx(next);
      setIsFading(false);

      // 4. Unlock interactions after fade-in finishes
      setTimeout(() => setIsAnimating(false), 400);
    }, 400);
  };

  // Automatically cycle through slides
  useEffect(() => {
    const timer = setInterval(() => {
      goTo((activeIdx + 1) % slides.length);
    }, 5500); // 5.5 seconds per slide
    return () => clearInterval(timer);
  }, [activeIdx, isAnimating]);

  // Dynamic style for smooth stagger animations
  const fadeStyle = (extraDelay = 0): React.CSSProperties => ({
    opacity: isFading ? 0 : 1,
    transform: isFading ? "translateY(15px)" : "translateY(0)",
    transition: `opacity 400ms ease ${isFading ? 0 : extraDelay}ms, transform 400ms ease ${isFading ? 0 : extraDelay}ms`,
  });

  return (
    <section
      id="home"
      className="relative w-full overflow-hidden font-sans min-h-screen flex flex-col justify-center"
      style={{ background: "#1a1f2e" }}
    >
      {/* ── BACKGROUNDS (Native CSS Crossfade) ── */}
      {slides.map((s, i) => (
        <div
          key={`bg-${i}`}
          className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
          style={{ opacity: i === activeIdx ? 1 : 0 }}
        >
          <img src={s.bg} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/40" />{" "}
          {/* Darkened slightly for better text contrast */}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-black/20" />
        </div>
      ))}

      {/* ── CENTERED CONTENT ── */}
      <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 md:px-10 w-full max-w-[1000px] mx-auto pt-10">
        {/* Badge */}
        <div
          style={fadeStyle(0)}
          className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-8"
        >
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#0077b6] opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-[#0077b6]" />
          </span>
          <span className="text-[11px] xl:text-xs tracking-[0.3em] uppercase text-blue-200 font-medium">
            {t.badge}
          </span>
        </div>

        {/* Dynamic Heading / Description Text */}
        <div
          style={fadeStyle(80)}
          className="min-h-[180px] sm:min-h-[140px] flex items-center justify-center mb-10"
        >
          {contentIdx === 0 ? (
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-[4rem] tracking-wide font-bold text-white leading-[1.15] drop-shadow-lg">
              {t.heading}
            </h1>
          ) : (
            <h2 className="text-xl sm:text-3xl md:text-5xl lg:text-[2.5rem] font-medium text-white leading-relaxed md:leading-snug max-w-4xl drop-shadow-lg">
              {t.subheading}
            </h2>
          )}
        </div>

        {/* CTAs */}
        <div
          style={fadeStyle(160)}
          className="flex flex-col sm:flex-row gap-4 justify-center items-center w-full sm:w-auto"
        >
          <a
            href="#membership"
            onClick={(e) => {
              e.preventDefault();
              document
                .querySelector("#membership")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full sm:w-auto inline-flex justify-center items-center gap-2 px-8 py-3.5 text-white font-medium text-sm md:text-base rounded-xl cursor-pointer transition-all"
            style={{
              background: "#0077b6",
              boxShadow: "0 4px 14px rgba(201,149,42,0.35)",
            }}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                "#0077b6";
              (e.currentTarget as HTMLAnchorElement).style.transform =
                "translateY(-2px)";
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLAnchorElement).style.background =
                "#0077b6";
              (e.currentTarget as HTMLAnchorElement).style.transform =
                "translateY(0)";
            }}
          >
            {t.ctaPrimary}
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              viewBox="0 0 24 24"
            >
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </a>
          <a
            href="#trade"
            onClick={(e) => {
              e.preventDefault();
              document
                .querySelector("#trade")
                ?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full sm:w-auto px-8 py-3.5 border border-white/40 text-white font-medium text-sm md:text-base rounded-xl hover:bg-white/10 hover:border-white/70 transition-colors text-center cursor-pointer"
          >
            {t.ctaSecondary}
          </a>
        </div>
      </div>

      {/* ── DOTS NAVIGATION ── */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 z-20 flex gap-3 items-center">
        {slides.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Slide ${i + 1}`}
            className={`h-[3px] rounded-full border-none cursor-pointer transition-all duration-500 ${
              i === activeIdx
                ? "bg-blue-400 w-12"
                : "bg-white/40 w-6 hover:bg-white/70"
            }`}
          />
        ))}
      </div>
    </section>
  );
}